import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import AuthShell from './AuthShell';
import { apiRequest } from '../../utils';

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 20;
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\])[A-Za-z\d!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\]{8,20}$/;

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (newPassword.length < MIN_PASSWORD_LENGTH || newPassword.length > MAX_PASSWORD_LENGTH) {
      setError(t('passwordLengthError', { min: MIN_PASSWORD_LENGTH, max: MAX_PASSWORD_LENGTH }));
      setShowToast(true);
      return;
    }

    if (!PASSWORD_PATTERN.test(newPassword)) {
      setError(t('passwordPatternError'));
      setShowToast(true);
      return;
    }

    setSubmitting(true);
    const formData = new FormData(event.target);
    const values = Object.fromEntries(formData.entries());
    const resetToken = sessionStorage.getItem('reset_token');

    try {
      const data = await apiRequest('POST', '/reset-password', { ...values, reset_token: resetToken });
      setError(null);
      setSuccessMessage(data.message);
      setShowToast(true);
      sessionStorage.removeItem('reset_token');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.message);
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('resetPassword')}
      subtitle={t('resetPasswordSubtitle', { defaultValue: 'Pick a new password to secure your account.' })}
    >
      <form className="auth__card" onSubmit={handleResetPassword}>
        <p className="auth__hint">{t('passwordPatternError')}</p>

        <div className="auth__field">
          <label className="auth__label">{t('newPassword')}</label>
          <div className="auth__input-wrap">
            <input
              className="auth__input auth__input--with-eye"
              type={showPassword ? 'text' : 'password'}
              name="new_password"
              placeholder={t('enterNewPassword')}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={MAX_PASSWORD_LENGTH}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="auth__eye"
              onClick={() => setShowPassword((v) => !v)}
              aria-label="Toggle password visibility"
            >
              <Icon name={showPassword ? 'eye-crossed' : 'eye'} />
            </button>
          </div>
        </div>

        <button type="submit" className="auth__primary" disabled={submitting}>
          {submitting ? <span className="auth__spinner" /> : <Icon name="lock" />}
          {t('resetPassword')}
        </button>

        <Toast
          showToast={showToast}
          setShowToast={setShowToast}
          message={successMessage ? t(successMessage) : t(error)}
          icon={successMessage ? 'check-circle' : 'exclamation-circle'}
        />
      </form>
    </AuthShell>
  );
};

export default ResetPassword;
